// Merging several inventory records of the same stock into one, and changing
// the count a batch record holds. Shared by the Inventory page, which states
// what is in the way before anyone presses Merge, and by the API, which
// refuses the same things.
//
// Records of one product pile up as separate rows with their own unit counts:
// a PO line acknowledged in batches, an InvenTree import, a split that is not
// wanted any more. Units can only share a record when nothing tells them apart,
// so everything a record says about where its units are and what they are for
// must already be the same.

// Records in one merge. Each one merged away is bound a few times in the
// statements that move its references, and D1 allows 100 bound values a query.
export const MERGE_MAX = 40;

// A count higher than this is a typing slip rather than stock on a shelf.
export const COUNT_MAX = 100000;

const units = (i) => Math.max(1, parseInt(i?.quantity, 10) || 1);
const label = (i) => i?.asset_tag || 'A record';

/** Total units across the records. */
export function totalUnits(items) {
  return (items || []).reduce((n, i) => n + units(i), 0);
}

/**
 * The record that keeps its tag unless the person picks another: the one
 * holding the most units, whose label is most likely the one on the box, and
 * the lowest tag among equals.
 */
export function defaultKeeper(items) {
  return [...(items || [])].sort((a, b) =>
    units(b) - units(a) || String(a.asset_tag || '').localeCompare(String(b.asset_tag || '')))[0] || null;
}

/** Why these records cannot become one, as sentences. Empty when they can. */
export function mergeProblems(items) {
  const list = items || [];
  if (list.length < 2) return ['Pick at least two records to merge.'];
  if (list.length > MERGE_MAX) return [`${list.length} records is more than can be merged at once (${MERGE_MAX} at most).`];
  const problems = [];

  // Two different catalogue products never merge. Records not linked to the
  // catalogue are matched by name.
  const linked = new Set(list.map(i => i.product_catalog_id).filter(Boolean));
  const names = new Set(list.map(i => String(i.name || '').trim().toLowerCase()));
  if (linked.size > 1 || (list.some(i => !i.product_catalog_id) && names.size > 1)) {
    problems.push('They are not all the same product.');
  }

  const serial = list.find(i => i.serial_number);
  if (serial) problems.push(`${label(serial)} has a serial number, so it is one particular unit and stays its own record.`);

  const statuses = new Set(list.map(i => i.status));
  if (statuses.has('incoming')) {
    problems.push('Incoming stock is received against its PO record by record. Merge it once it has arrived.');
  } else if (statuses.has('decommissioned')) {
    problems.push('Decommissioned records are no longer stock, so there is nothing to merge them into.');
  } else if (statuses.size > 1) {
    problems.push('They do not all have the same status.');
  }

  if (new Set(list.map(i => i.location_id || null)).size > 1) problems.push('They are not all in the same location.');
  if (new Set(list.map(i => i.allocated_to_sow_id || null)).size > 1) problems.push('They are not all allocated to the same SOW.');
  else if (new Set(list.map(i => i.sow_hardware_line_id || null)).size > 1) problems.push('They fulfil different SOW hardware lines.');
  return problems;
}

/** Why the count of this record cannot be changed here, or null when it can. */
export function countProblem(item) {
  if (!item) return 'Record not found.';
  if (item.serial_number) return `${label(item)} has a serial number, so it is one particular unit.`;
  if (item.status === 'incoming') return 'Incoming stock is counted in when its PO is delivered.';
  if (item.status === 'decommissioned') return 'Decommissioned records are no longer stock.';
  return null;
}

/**
 * The notes with any "Batch qty: N" part brought up to the new count. Batch
 * acknowledgement writes that part, and a figure left behind would contradict
 * the quantity beside it.
 */
export function withBatchQty(notes, qty) {
  if (!notes) return notes ?? null;
  return notes.split(' · ').map(p => /^Batch qty: \d+$/.test(p) ? `Batch qty: ${qty}` : p).join(' · ');
}
